import React, { useCallback, useEffect, useState } from 'react';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { TeamMemberUiModel, TeamUiModel } from '../../../shared/ui-models/team.ui-model';
import {
  archiveTeamMember,
  createTeamMember,
  getTeamMemberTree,
  getTeams,
  updateTeamMember
} from '../../../shared/services/data/team.data';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { LoadingData } from '../../../ui/LoadingData';
import { NotFoundData } from '../../../ui/NotFoundData';
import {
  describeLoadError,
  LoadError
} from '../../../shared/components/core-ui/load-error/load-error.component';
import { MemberFormModal, MemberFormValue } from './components/member-form-modal.component';
import {
  CHIP,
  ROLE_STYLE as TEAM_ROLE_STYLE,
  styleFor
} from '../../../LeadModule/shared/lead-visuals';

const ROLE_LABEL: Record<string, string> = {
  manager: 'Manager',
  lead: 'Team Lead',
  senior: 'Senior',
  agent: 'Agent'
};

// Seniority reads at a glance, darkest at the top of the tree.
const ROLE_STYLE = TEAM_ROLE_STYLE;

interface MemberRowProps {
  member: TeamMemberUiModel;
  depth: number;
  onEdit: (member: TeamMemberUiModel) => void;
  onArchive: (member: TeamMemberUiModel) => void;
}

const MemberRow: React.FC<MemberRowProps> = ({ member, depth, onEdit, onArchive }) => (
  <>
    <tr className="hover:bg-creams-100">
      <td className="border-t border-gray-100 px-3 py-3">
        <div className="flex items-center gap-2" style={{ paddingLeft: `${depth * 22}px` }}>
          {depth > 0 ? <span className="text-neutral-300">└</span> : null}
          <span className="font-gilroy-medium text-neutral-500">{member.name}</span>
          {member.isOrphan ? (
            // Shown, not hidden. Their manager is missing from the tree, and a
            // row quietly dropped is a person nobody notices has no oversight.
            <span
              className={`${CHIP} bg-creams-500 text-creams-900`}
              title="Reports to someone who is not in this tree"
            >
              Unattached
            </span>
          ) : null}
        </div>
      </td>
      <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
        <span className={`${CHIP} ${styleFor(ROLE_STYLE, member.role)}`}>
          {ROLE_LABEL[member.role] ?? member.role}
        </span>
      </td>
      <td className="border-t border-gray-100 px-3 py-3 text-neutral-400">
        {member.teamNames.length ? member.teamNames.join(', ') : '—'}
      </td>
      <td className="border-t border-gray-100 px-3 py-3 text-neutral-400">{member.email || '—'}</td>
      <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap text-right">
        <button type="button" className="text-sm text-primary-500" onClick={() => onEdit(member)}>
          Edit
        </button>
        <button
          type="button"
          className="ml-3 text-sm text-action-danger"
          onClick={() => onArchive(member)}
        >
          Archive
        </button>
      </td>
    </tr>
    {member.children.map((child: TeamMemberUiModel) => (
      <MemberRow
        key={child.userId}
        member={child}
        depth={depth + 1}
        onEdit={onEdit}
        onArchive={onArchive}
      />
    ))}
  </>
);

const countMembers = (nodes: TeamMemberUiModel[]): number =>
  nodes.reduce(
    (total: number, node: TeamMemberUiModel) => total + 1 + countMembers(node.children),
    0
  );

export const TeamStructureContainer: React.FC = () => {
  const [tree, setTree] = useState<TeamMemberUiModel[]>([]);
  const [teams, setTeams] = useState<TeamUiModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TeamMemberUiModel | undefined>();

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([getTeamMemberTree(), getTeams()]).then(([treeResult, teamsResult]) => {
      setLoadError(isErrorInfo(treeResult) ? describeLoadError(treeResult) : undefined);
      if (!isErrorInfo(treeResult)) setTree(treeResult);
      if (!isErrorInfo(teamsResult)) setTeams(teamsResult);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = (value: MemberFormValue) => {
    const isEdit = Boolean(editing);
    const request = isEdit
      ? updateTeamMember(value.userId, {
          role: value.role,
          team: value.team,
          reportsTo: value.reportsTo
        })
      : createTeamMember({
          userId: value.userId,
          role: value.role,
          team: value.team,
          reportsTo: value.reportsTo
        });

    const promise = request.then((response: unknown) => {
      if (isErrorInfo(response as ErrorInfo)) throw response;
      setShowForm(false);
      setEditing(undefined);
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: isEdit ? 'Saving member...' : 'Adding member...',
      successMessage: isEdit ? 'Member saved' : 'Member added',
      errorPermissionMessage: 'You do not have permission to change the team structure',
      errorFailedMessage: isEdit ? 'Failed to save member' : 'Failed to add member'
    });
  };

  const handleArchive = (member: TeamMemberUiModel) => {
    const promise = archiveTeamMember(member.userId).then((response: boolean | ErrorInfo) => {
      if (isErrorInfo(response)) throw response;
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: 'Archiving member...',
      successMessage: 'Member archived',
      errorPermissionMessage: 'You do not have permission to archive a member',
      // The service refuses while they still have direct reports, and says so.
      // A generic message would hide the one thing that fixes it.
      errorFailedMessage: 'Could not archive — move their direct reports first'
    });
  };

  return (
    <div className="flex h-full flex-col gap-5 md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <section className="flex flex-wrap items-center gap-3">
        <span className="h-8 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
        <h1 className="font-gilroy-bold text-2xl text-neutral-500">Team structure</h1>
        {!loading ? (
          <span className={`${CHIP} bg-primary-50 text-primary-700`}>
            {countMembers(tree)} member{countMembers(tree) === 1 ? '' : 's'}
          </span>
        ) : null}
        <span className="ml-auto">
          <Button
            type={ButtonType.primary}
            onClick={() => {
              setEditing(undefined);
              setShowForm(true);
            }}
          >
            Add member
          </Button>
        </span>
      </section>

      <section>
        {loading ? (
          <LoadingData />
        ) : loadError ? (
          <LoadError message={loadError} onRetry={load} />
        ) : tree.length === 0 ? (
          <NotFoundData />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-md">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="bg-creams-200">
                  {['Member', 'Role', 'Teams', 'Email', ''].map((column: string) => (
                    <th
                      key={column || 'actions'}
                      className="whitespace-nowrap px-3 py-3 font-gilroy-bold text-neutral-500"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tree.map((root: TeamMemberUiModel) => (
                  <MemberRow
                    key={root.userId}
                    member={root}
                    depth={0}
                    onEdit={(member: TeamMemberUiModel) => {
                      setEditing(member);
                      setShowForm(true);
                    }}
                    onArchive={handleArchive}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <MemberFormModal
        show={showForm}
        member={editing}
        teams={teams}
        onClose={() => {
          setShowForm(false);
          setEditing(undefined);
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
